import {
  accountActivationApi,
  accountAuthenticationApi,
  accountRestApi,
  emailChangeApi,
  passwordResetApi,
  pukApi
} from '@/stores/accountApiClient'
import { partnerApi } from '@/stores/partnerApiClient'
import type {
  AccountInformation,
  ActivationInformation,
  OrchestrateAccountsResponse,
  PartnerAddress,
  ServiceResponseAccountIdForPasswordResetResponse
} from 'account-api'
import type { Partner } from 'partner-api'
import type { InitialCredentialsRequest } from '@/types/account-management/AccountManagementTypes'

export { getCsrfAccountManagementToken } from '@/stores/accountApiClient'

// ==========================================================================
// Account-Informationen und -Verwaltung
// ==========================================================================

/**
 * Public
 * Erstellt ein Konto für einen bestehenden Kunden
 */
export async function createAccountForExistingCustomer(
  vsnr: string,
  email: string
): Promise<OrchestrateAccountsResponse | undefined> {
  return accountAuthenticationApi().authenticate({
    authenticationRequest: { vsnr, email }
  })
}

/**
 * Public
 * Setzt initiale Anmeldedaten für ein Konto
 */
export async function setInitialCredentials(
  accountId: string,
  credentials: InitialCredentialsRequest
): Promise<void> {
  await accountActivationApi().activateBenutzerkonto({
    benutzerkontoId: accountId,
    activationPayload: {
      username: credentials.username,
      password: credentials.password,
      emailCode: credentials.emailCode,
      birthdate: credentials.birthdate,
      vsnr: credentials.vsnr,
      newEmail: credentials.newEmail,
      termsAndConditionsAccepted: credentials.termsAndConditionsAccepted
    }
  })
}

/**
 * Public
 * Benutzernamen anhand der Policen-ID und des Geburtsdatums abrufen
 */
export async function retrieveUsernames(
  policyId: string,
  birthdate: string
): Promise<string[]> {
  const response = await accountRestApi(false).getUsernames({
    policyId,
    birthdate
  })
  return response.data ?? []
}

/**
 * Funktion zum Abrufen der Accounts-Informationen für ID Level Box
 */
export async function fetchAccountsByPPID(
  partnerProxyId: string
): Promise<AccountInformation[]> {
  const response = await accountRestApi(true).getAccounts({ partnerProxyId })
  return response.data?.accounts ?? []
}

/**
 * Erstellt ein Konto für einen bestehenden Kunden mit alternativer Methode
 */
export async function createAccountForExistingCustomerAlternative(
  vsnr: string,
  email: string,
  postcode: string,
  birthdate: string
): Promise<OrchestrateAccountsResponse | undefined> {
  return accountAuthenticationApi().authenticate({
    authenticationRequest: { vsnr, email, postcode, birthdate }
  })
}

/**
 * Löscht ein Konto nach Konto-ID
 */
export async function deleteAccount(accountId: string): Promise<void> {
  await accountRestApi(true).deleteBenutzerkonto({ benutzerkontoId: accountId })
}

// ==========================================================================
// Authentifizierung und Passwort-Management
// ==========================================================================

/**
 * Validiert einen Passwort-Reset-Code mit Benutzername und Geburtsdatum
 */
export async function validatePasswordResetCode(
  resetCode: string,
  username: string,
  birthdate?: string
): Promise<ServiceResponseAccountIdForPasswordResetResponse> {
  return passwordResetApi().getAccountIdForResetCode({
    resetCode,
    username,
    birthdate: birthdate as string
  })
}

/**
 * Initiiert einen Passwort-Reset-Prozess
 */
export async function initiatePasswordReset(
  username: string,
  resetDueToMigration: boolean = false
): Promise<void> {
  await passwordResetApi().initiatePublicPasswordReset({
    passwordResetInitializationRequest: { username, resetDueToMigration }
  })
}

/**
 * Setzt ein neues Passwort mit einem gültigen Reset-Code
 */
export async function setNewPassword(
  resetCode: string,
  accountId: string,
  newPassword: string
): Promise<void> {
  await passwordResetApi().changePassword({
    resetCode,
    passwordResetRequest: { kontoId: accountId, newPassword }
  })
}

// ==========================================================================
// ID-Level und Verifizierung
// ==========================================================================

/**
 * Public
 * Erhöht die ID-Level eines Kontos durch Validierung eines PUK-Codes
 */
export async function upgradeAccountIdLevel(
  accountId: string,
  puk: string
): Promise<void> {
  await pukApi(false).upgradeIdLevel({
    benutzerkontoId: accountId,
    idLevelUpgradePayload: { puk }
  })
}

/**
 * Public
 * Funktion zum Senden des automatischen PUK für nicht eingeloggte Benutzer
 */
export async function sendUnlockCodeLoggedOut(
  accountId: string,
  partnerProxyId: string
): Promise<void> {
  await pukApi(false).automaticPukCreation({
    benutzerkontoId: accountId,
    automaticPukRequest: { partnerProxyId }
  })
}

/**
 * Public
 * Sendet einen Unlock-Code (PUK) per SMS für nicht eingeloggte Benutzer
 */
export async function sendUnlockCodeBySmsLoggedOut(
  accountId: string,
  phoneNumber: string,
  birthdate: string
): Promise<void> {
  await pukApi(false).createSmsPukActivation({
    benutzerkontoId: accountId,
    birthdate,
    createSmsPukRequest: { phoneNumber }
  })
}

/**
 * Sendet einen Unlock-Code (PUK) per SMS für eingeloggte Benutzer
 */
export async function sendUnlockCodeBySmsLoggedIn(
  accountId: string,
  phoneNumber: string
): Promise<void> {
  await pukApi(true).createSmsPuk({
    benutzerkontoId: accountId,
    createSmsPukRequest: { phoneNumber }
  })
}

/**
 * Funktion zum Senden des Unlock-Codes für eingeloggte Benutzer
 */
export async function sendUnlockCodeLoggedIn(
  accountId: string,
  partnerAddressId: number
): Promise<void> {
  await pukApi(true).createPuk({
    benutzerkontoId: accountId,
    createPukRequest: { partnerAddressId }
  })
}

// ==========================================================================
// E-Mail-Verwaltung
// ==========================================================================

/**
 * Neue Funktion für die E-Mail-Code-Bestätigung
 */
export async function confirmEmailCodeChange(
  accountId: string,
  emailCode: string
): Promise<void> {
  await emailChangeApi().confirmEmailChange({
    accountId,
    changeEmailConfirmation: { emailCode }
  })
}

/**
 * Neue Funktion für die E-Mail-Adressänderung
 *
 * Hinweis: Das Backend antwortet auf diesen Endpoint mit HTTP 202 und leerem Body.
 * Der generierte Client (`initiateEmailChange`) versucht dennoch, den Body als JSON
 * zu parsen (`ServiceResponseVoid`), was bei leerem Body zu einem
 * "Unexpected end of JSON input" führt. Daher wird hier die `Raw`-Variante genutzt,
 * die den Response-Status prüft, ohne den (leeren) Body zu parsen.
 */
export async function updateEmailAddress(
  accountId: string,
  newEmailAddress: string
): Promise<void> {
  // await emailChangeApi().initiateEmailChangeRaw({
  await emailChangeApi().initiateEmailChange({
    accountId,
    changeEmailRequest: { newEmailAddress }
  })
}

/**
 * Ändert den Benutzernamen eines eingeloggten Kontos.
 */
export async function updateUsername(
  accountId: string,
  newUsername: string
): Promise<void> {
  await accountRestApi(true).changeUsername({
    accountId,
    changeUsernameRequest: { newUsername }
  })
}

/**
 * Sendet erneut eine Aktivierungs-E-Mail an ein vorbereitetes Konto
 */
export async function resendActivationEmail(accountId: string): Promise<void> {
  await accountRestApi(true).resendActivationEmail({ benutzerkontoId: accountId })
}

// ==========================================================================
// Partner-bezogene Funktionen
// ==========================================================================

/**
 * Public
 * Funktion zum Abrufen der Partner-Adresse für nicht eingeloggte Benutzer
 */
export async function getPartnerAddressForLoggedOutUser(
  accountId: string,
  birthdate: string
): Promise<PartnerAddress> {
  return accountActivationApi().retrievePartnerAddress({
    benutzerkontoId: accountId,
    birthdate
  })
}

/**
 * Lädt die vorbereiteten Aktivierungs-Informationen anhand des E-Mail-Codes
 */
export async function retrievePreparedAccount(
  emailCode: string
): Promise<ActivationInformation> {
  return accountActivationApi().retrievePreparedBenutzerkonto({ emailcode: emailCode })
}

/**
 * Funktion zum Abrufen des Partners nach partnerProxyId
 * (PARTNER_PROXY-Service, ueber generierten partner-api Client)
 */
export async function getPartnerByPartnerProxyId(
  partnerProxyId: string
): Promise<Partner | undefined> {
  const response = await partnerApi().getPartner({ partnerProxyId })
  return response.data
}
